type DynamicFields = {
  fieldName: string;
  docName: string;
};

export const convertDocumentData = (
  data: any,
  docArrayName: string = "document",
) => {
  if (!data || !data[docArrayName] || !Array.isArray(data[docArrayName])) {
    data[docArrayName] = [];
    return data;
  }

  for (const key in data) {
    if (
      key.includes("documentName-") ||
      key.includes("date-") ||
      key.includes("memorialNo-") ||
      key.includes("remarks-")
    ) {
      const suffix = key.substring(key.indexOf("-") + 1);
      const matchedDocument = data[docArrayName].find(
        (doc: any) => doc.documentId === suffix,
      );
      if (matchedDocument) {
        const propName = key.substring(0, key.indexOf("-"));
        matchedDocument[propName] = data[key];
        delete data[key];
      }
    }
  }
  return data;
};

export const convertSingleDocumentData = (
  data: any,
  docObjectName: string = "document",
) => {
  if (
    !data ||
    !data[docObjectName] ||
    typeof data[docObjectName] !== "object"
  ) {
    return data;
  }

  const documentDetails = data[docObjectName];
  const transformedData = { ...data };
  transformedData[docObjectName] = {
    ...documentDetails,
    documentName: data.documentName || "",
    remarks: data.remarks || "",
    memorialNo: data.memorialNo || "",
    date: data.date || "",
  };

  delete transformedData.documentName;
  delete transformedData.remarks;
  delete transformedData.memorialNo;
  delete transformedData.date;

  return transformedData;
};

export const convertMultiDocumentSingleFileData = (
  data: any,
  docArrName: string[],
) => {
  const rawData = { ...data };

  if (!rawData) {
    return rawData;
  }

  let convertedData = {};

  docArrName.forEach((doc) => {
    const documentDetails = rawData[doc];

    if (documentDetails) {
      rawData[doc] = {
        ...documentDetails,
        documentName:
          rawData[`documentName-${documentDetails.documentId}`] || "",
        remarks: rawData[`remarks-${documentDetails.documentId}`] || "",
        memorialNo: rawData[`memorialNo-${documentDetails.documentId}`] || "",
        date: rawData[`date-${documentDetails.documentId}`] || "",
      };

      delete rawData[`documentName-${documentDetails.documentId}`];

      delete rawData[`remarks-${documentDetails.documentId}`];
      delete rawData[`memorialNo-${documentDetails.documentId}`];
      delete rawData[`date-${documentDetails.documentId}`];

      convertedData = { ...rawData };
    } else {
      convertedData = { ...rawData };
    }
  });
  return convertedData;
};

export const revertMultiDocumentSingleFileData = (
  data: any,
  docArrName: string[],
) => {
  const rawData = { ...data };

  if (!rawData) {
    return rawData;
  }

  let convertedData = {};

  docArrName.forEach((doc) => {
    const documentDetails = rawData[doc];

    if (documentDetails) {
      rawData[`documentName-${documentDetails.documentId}`] =
        documentDetails.documentName;

      rawData[`remarks-${documentDetails.documentId}`] =
        documentDetails.remarks;

      rawData[`memorialNo-${documentDetails.documentId}`] =
        documentDetails.memorialNo;

      rawData[`date-${documentDetails.documentId}`] = documentDetails.date;

      rawData[doc] = { ...documentDetails };
      convertedData = { ...rawData };
    } else {
      convertedData = { ...rawData };
    }
  });

  return convertedData;
};

export const revertDocumentData = (originalData: any) => {
  const updatedData = { ...originalData };

  if (updatedData.document && updatedData.document.length > 0) {
    updatedData.document.forEach((documentItem: any) => {
      const documentFields = { ...documentItem };

      Object.keys(documentFields).forEach((key) => {
        if (key !== "documentId") {
          updatedData[`${key}-${documentItem.documentId}`] =
            documentFields[key];
        }
      });
    });

    delete updatedData.document;
  }

  return { ...originalData, ...updatedData };
};

export const revertSingleDocumentData = (
  data: any,
  docObjectName: string = "document",
) => {
  if (
    !data ||
    !data[docObjectName] ||
    typeof data[docObjectName] !== "object"
  ) {
    return data;
  }

  const { id, title, description, [docObjectName]: documentDetails } = data;
  const { memorialNo, remarks, date, documentName } = documentDetails || {};

  const extractedData = {
    id,
    title,
    description,
    [docObjectName]: documentDetails || {},
    memorialNo,
    remarks,
    date,
    documentName,
  };

  return { ...data, ...extractedData };
};

export const convertMultipleDocumentData = (
  data: any,
  docArrayNames: string[],
) => {
  if (!data) {
    return data;
  }

  for (let i = 0; i < docArrayNames.length; i++) {
    for (const key in data) {
      if (
        key.includes("documentName-") ||
        key.includes("date-") ||
        key.includes("memorialNo-") ||
        key.includes("remarks-") ||
        key.includes("documentType-") ||
        key.includes("fileId-") ||
        key.includes("id-") ||
        key.includes("mimeType-")
      ) {
        const suffix = key.substring(key.indexOf("-") + 1);
        const matchedDocument = data[docArrayNames[i]]?.find(
          (doc: any) => doc.documentId === suffix,
        );

        if (matchedDocument) {
          const propName = key.substring(0, key.indexOf("-"));
          matchedDocument[propName] = data[key];
          delete data[key];
        }
      } else if (!data[docArrayNames[i]]) {
        data[docArrayNames[i]] = [];
      }
    }
  }
  return data;
};

export const revertMultipleDocumentData = (
  originalData: any,
  docArrayNames: string[],
  dynamicFields?: DynamicFields[],
) => {
  const updatedData = { ...originalData };

  for (let i = 0; i <= docArrayNames.length; i++) {
    const docArray = updatedData[docArrayNames[i]];

    if (docArray && docArray?.length > 0) {
      // looping inside doc array
      docArray.forEach((documentItem: any) => {
        const documentFields = { ...documentItem };
        Object.keys(documentFields).forEach((key) => {
          if (key !== "documentId") {
            updatedData[`${key}-${documentItem.documentId}`] =
              documentFields[key];
          }
        });
      });
    } else if (docArray?.length === 0) {
      continue;
    }
  }

  if (dynamicFields && dynamicFields.length > 0) {
    for (let i = 0; i < dynamicFields.length; i++) {
      const dynamicField = updatedData[dynamicFields[i]?.fieldName];

      if (dynamicField && dynamicField?.length > 0) {
        // looping inside doc array
        dynamicField?.forEach((fields: any) => {
          fields[dynamicFields[i]?.docName]?.forEach((element: any) => {
            const documentFields = { ...element };
            Object.keys(documentFields).forEach((key) => {
              if (key !== "documentId") {
                updatedData[`${key}-${element.documentId}`] =
                  documentFields[key];
              }
            });
          });
        });
      } else if (dynamicField?.length === 0) {
        continue;
      }
    }
  }

  return { ...originalData, ...updatedData };
};

export const revertSyncData = (data: any) => {
  const updatedData = { ...data };

  ["seal", "signature", "photo"].forEach((key) => {
    const documentId = updatedData[key]?.documentId;

    if (documentId) {
      const propsToMove = ["memorialNo", "remarks", "documentName", "date"];
      propsToMove.forEach((prop) => {
        if (
          updatedData[key][prop] !== undefined &&
          updatedData[key][prop] !== null
        ) {
          updatedData[`${prop}-${documentId}`] = updatedData[key][prop];
          delete updatedData[key][prop];
        }
      });
    }
  });

  return updatedData;
};

export const restructureDocumentsData = (data: any) => {
  const transformedData: any = {};

  const propertiesToRestructure = [
    "memorialNo",
    "remarks",
    "documentName",
    "date",
  ];

  data?.forEach((doc: any) => {
    propertiesToRestructure.forEach((property) => {
      const key = `${property}-${doc.documentId}`;
      transformedData[key] = doc[property];
    });
  });

  return transformedData;
};
